/**
 * customer-bootstrap · 数据 / API / WebSocket
 *
 * 集中所有副作用：
 *   ✓ XHR (POST /api/table-occupy)
 *   ✓ WebSocket（强制登出推送）
 *   ✓ session/localStorage 写入（强制登出时清会话）
 *   ✗ 不直接 querySelector 业务 DOM；仅消费 utils 提供的纯数据
 */

import {
  DEFAULT_PWD,
  FORCE_LOGOUT_RECONNECT_MS,
  RELOAD_DELAY_MS,
  STORAGE_KEYS,
  type CustomerSession,
  type ForceLogoutMessage,
  type OrdersystemFacade,
} from './types';
import {
  apiBaseToWsUrl,
  isValidPin,
  readNetworkConfig,
  readSystemSettings,
  safeParse,
} from './utils';

/** 当前生效的密码（系统设置无填或非法 → fallback 123456） */
export const readGatePassword = (): string => {
  const raw = readSystemSettings()?.customerLiteLoginPassword;
  const v = raw == null ? '' : String(raw).trim();
  return isValidPin(v) ? v : DEFAULT_PWD;
};

/** 读当前会话（sessionStorage 优先，找不到回落 localStorage） */
export const readCurrentSession = (): CustomerSession | null => {
  try {
    const ss = sessionStorage.getItem(STORAGE_KEYS.customerSession);
    if (ss) return safeParse<CustomerSession>(ss);
  } catch {
    /* noop */
  }
  try {
    return safeParse<CustomerSession>(localStorage.getItem(STORAGE_KEYS.customerSession));
  } catch {
    return null;
  }
};

/** 找 Ordersystem facade（兼容 window.Ordersystem 与 OS.facade 两种暴露） */
const facade = (): OrdersystemFacade | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window.Ordersystem ?? window.OS?.facade ?? undefined;
};

/**
 * 解析当前 API base：
 *   1) 先问 facade.getNetworkConfig()
 *   2) 回落 localStorage.restaurantNetworkConfig
 *   3) 仍空 + 当前是 http(s) → 用 location.origin
 */
const resolveApiBase = (): string => {
  const fromFacade = facade()?.getNetworkConfig?.()?.apiBaseUrl;
  if (fromFacade != null) {
    const v = String(fromFacade).trim();
    if (v) return v;
  }
  const fromLs = readNetworkConfig()?.apiBaseUrl;
  if (fromLs != null) {
    const v = String(fromLs).trim();
    if (v) return v;
  }
  if (/^https?:/i.test(location.protocol)) return String(location.origin || '').trim();
  return '';
};

/**
 * POST /api/table-occupy
 * 用 XHR 不用 fetch：iOS 12 Safari 在 lockdown 状态下 fetch 容易被中断，XHR 更稳。
 */
export const notifyTableOccupied = (): void => {
  const sess = readCurrentSession();
  const table = String(sess?.table ?? '').trim();
  if (!table) return;

  const base = resolveApiBase().replace(/\/$/, '');
  if (!base) return;

  try {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${base}/api/table-occupy`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(
      JSON.stringify({
        table,
        guests: typeof sess?.guests === 'number' ? sess.guests : null,
        guest: typeof sess?.guestName === 'string' ? sess.guestName : '',
      }),
    );
  } catch {
    /* 静默失败：scheduleRestoreNotify 会在 800ms 再补一次 */
  }
};

/* ─────────── 强制登出 WebSocket ─────────── */

interface ForceLogoutHandle {
  socket: WebSocket | null;
  url: string;
  reloading: boolean;
}

const handle: ForceLogoutHandle = { socket: null, url: '', reloading: false };

const onMessage = (ev: MessageEvent): void => {
  if (handle.reloading) return;
  const raw = typeof ev.data === 'string' ? ev.data : null;
  const msg = safeParse<ForceLogoutMessage>(raw);
  if (!msg || msg.type !== 'table-history-cleared') return;

  const cleared = String(msg.table ?? '').trim();
  const mine = String(readCurrentSession()?.table ?? '').trim();
  if (!cleared || cleared !== mine) return;

  handle.reloading = true;
  try {
    facade()?.clearCustomerSession?.();
  } catch {
    /* noop */
  }
  try {
    sessionStorage.removeItem(STORAGE_KEYS.customerSession);
  } catch {
    /* noop */
  }
  try {
    localStorage.removeItem(STORAGE_KEYS.customerSession);
  } catch {
    /* noop */
  }
  try {
    handle.socket?.close();
  } catch {
    /* noop */
  }
  setTimeout(() => {
    try {
      location.reload();
    } catch {
      /* noop */
    }
  }, RELOAD_DELAY_MS);
};

export const connectForceLogoutWs = (): void => {
  if (handle.reloading) return;
  if (typeof WebSocket === 'undefined') return;

  const url = apiBaseToWsUrl(resolveApiBase());
  if (!url) return;

  const sock = handle.socket;
  if (
    sock &&
    handle.url === url &&
    (sock.readyState === WebSocket.CONNECTING || sock.readyState === WebSocket.OPEN)
  ) {
    return;
  }

  handle.url = url;
  try {
    sock?.close();
  } catch {
    /* noop */
  }

  let next: WebSocket | null;
  try {
    next = new WebSocket(url);
  } catch {
    handle.socket = null;
    return;
  }
  handle.socket = next;

  next.addEventListener('message', onMessage);
  next.addEventListener('close', () => {
    if (handle.reloading) return;
    setTimeout(connectForceLogoutWs, FORCE_LOGOUT_RECONNECT_MS);
  });
};

/** 仅供测试：重置内部 handle（生产代码不应调用） */
export const __resetForceLogoutForTests = (): void => {
  try {
    handle.socket?.close();
  } catch {
    /* noop */
  }
  handle.socket = null;
  handle.url = '';
  handle.reloading = false;
};
